// gameUtils.ts

import { ModelType } from "./TicTacToeModelSelector";

export type Symbol = "x" | "o" | "_";
export type PlayerSymbol = "x" | "o";
export type Winner = "x" | "o" | "draw" | null;

export interface TicTacToeGame {
  board: Symbol[][];
  xModel: ModelType;
  oModel: ModelType;
}

export function generateGame(
  xModel: ModelType,
  oModel: ModelType,
  boardLength: number = 3
): TicTacToeGame {
  const board: Symbol[][] = Array.from({ length: boardLength }, () =>
    Array.from({ length: boardLength }, () => "_")
  );
  return {
    board,
    xModel,
    oModel,
  };
}

export function renderBoard(game: TicTacToeGame): string {
  const board = game.board;
  const boardLength = board.length;
  let boardStr =
    "   " +
    Array.from({ length: boardLength }, (_, i) => i + 1).join("   ") +
    "\n";

  for (let i = 0; i < boardLength; i++) {
    const rowLabel = String.fromCharCode(65 + i);
    const row = board[i].join(" | ");
    boardStr += `${rowLabel}  ${row}\n`;
  }

  return boardStr;
}

export function getOpponentMove(game: TicTacToeGame): [number, number] {
  const emptyCells: [number, number][] = [];
  for (let i = 0; i < game.board.length; i++) {
    for (let j = 0; j < game.board[i].length; j++) {
      if (game.board[i][j] === "_") {
        emptyCells.push([i, j]);
      }
    }
  }

  const choice = Math.floor(Math.random() * emptyCells.length);
  return emptyCells[choice];
}

export function applyMove(
  game: TicTacToeGame,
  moveXml: string,
  symbol: PlayerSymbol
): void {
  let square: string | null = null;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(moveXml, "text/xml");
    square = doc.documentElement.textContent;
  } catch {
    throw new Error("Invalid XML");
  }

  if (!square || square.length < 2) {
    throw new Error("Unable to parse square");
  }

  const rowIndex = square.charCodeAt(0) - 65;
  const colStr = square[1];
  const colIndex = parseInt(colStr, 10);

  if (isNaN(colIndex)) {
    throw new Error(`Invalid column number: ${colStr}`);
  }

  const boardLength = game.board.length;

  if (
    rowIndex < 0 ||
    rowIndex >= boardLength ||
    colIndex < 1 ||
    colIndex > boardLength
  ) {
    throw new Error(
      `Invalid move, row or column out of bounds: ${rowIndex}, ${colIndex}`
    );
  }

  if (game.board[rowIndex][colIndex - 1] !== "_") {
    throw new Error("Square already occupied");
  }

  game.board[rowIndex][colIndex - 1] = symbol;
}

export function checkWinner(board: Symbol[][]): Winner {
  const boardLength = board.length;

  // check rows
  for (const row of board) {
    if (row.every((cell) => cell === row[0] && cell !== "_")) {
      return row[0] as PlayerSymbol;
    }
  }

  // check columns
  for (let col = 0; col < boardLength; col++) {
    const column = board.map((row) => row[col]);
    if (column.every((cell) => cell === column[0] && cell !== "_")) {
      return column[0] as PlayerSymbol;
    }
  }

  // upward diagonal
  const upward = board.map((row, i) => row[boardLength - 1 - i]);
  if (upward.every((cell) => cell === upward[0] && cell !== "_")) {
    return upward[0] as PlayerSymbol;
  }

  // downward diagonal
  const downward = board.map((row, i) => row[i]);
  if (downward.every((cell) => cell === downward[0] && cell !== "_")) {
    return downward[0] as PlayerSymbol;
  }

  // check for draw
  const allFilled = board.flat().every((cell) => cell !== "_");
  if (allFilled) return "draw";

  return null;
}
